import { useState, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Icon } from "@/components/ui/icon";
import { toast } from "sonner";
import { ProjectForm } from "./ProjectForm";
import { DraggableList } from "./DraggableList";
import { BulkActionBar } from "./BulkActionBar";

export const ProjectsManager = () => {
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const { data: projects } = useQuery({
    queryKey: ["admin-projects"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("projects")
        .select("*")
        .order("display_order");
      if (error) throw error;
      return data;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("projects").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-projects"] });
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      toast.success("Project deleted successfully");
    },
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const { error } = await supabase
        .from("projects")
        .delete()
        .in("id", ids);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-projects"] });
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      setSelectedIds(new Set());
      toast.success("Selected projects deleted");
    },
  });

  const reorderMutation = useMutation({
    mutationFn: async (items: typeof projects) => {
      if (!items) return;
      for (let i = 0; i < items.length; i++) {
        const { error } = await supabase
          .from("projects")
          .update({ display_order: i })
          .eq("id", items[i].id);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-projects"] });
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      toast.success("Order updated");
    },
  });

  const toggleSelection = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleReorder = useCallback((newItems: NonNullable<typeof projects>) => {
    reorderMutation.mutate(newItems);
  }, [reorderMutation]);

  return (
    <div className="space-y-6">
      <Card className="p-6 bg-card border-border shadow-sm rounded-xl">
        <h2 className="text-2xl font-bold text-foreground mb-6">Add New Project</h2>
        <ProjectForm />
      </Card>

      <div className="space-y-4">
        <h3 className="text-xl font-semibold text-foreground">Existing Projects</h3>
        
        {projects && projects.length > 0 && (
          <BulkActionBar
            selectedCount={selectedIds.size}
            totalCount={projects.length}
            onSelectAll={() => setSelectedIds(new Set(projects.map((p) => p.id)))}
            onDeselectAll={() => setSelectedIds(new Set())}
            onDeleteSelected={() => bulkDeleteMutation.mutate(Array.from(selectedIds))}
            isDeleting={bulkDeleteMutation.isPending}
          />
        )}

        {projects && (
          <DraggableList
            items={projects}
            onReorder={handleReorder}
            disabled={editingId !== null}
            renderItem={(project) => (
              <Card className="p-6 bg-card border-border shadow-sm rounded-xl">
                {editingId === project.id ? (
                  <div className="space-y-4">
                    <ProjectForm
                      initialData={project}
                      onSuccess={() => setEditingId(null)}
                    />
                    <Button
                      variant="outline"
                      onClick={() => setEditingId(null)}
                      className="w-full"
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-start gap-4">
                    <Checkbox
                      checked={selectedIds.has(project.id)}
                      onCheckedChange={() => toggleSelection(project.id)}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold text-foreground">
                        {project.project_name}
                      </h4>
                      {project.description && (
                        <p className="text-muted-foreground mt-2 line-clamp-2">{project.description}</p>
                      )}
                      {project.technologies && (
                        <p className="text-sm text-primary mt-2">
                          {project.technologies.join(", ")}
                        </p>
                      )}
                      {(project.github_url || project.live_demo_url) && (
                        <div className="flex gap-2 mt-2 text-xs text-muted-foreground">
                          {project.github_url && <span>GitHub</span>}
                          {project.live_demo_url && <span>Live Demo</span>}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setEditingId(project.id)}
                      >
                        <Icon name="edit" size={16} />
                      </Button>
                      <Button
                        variant="destructive"
                        size="icon"
                        onClick={() => deleteMutation.mutate(project.id)}
                      >
                        <Icon name="delete" size={16} />
                      </Button>
                    </div>
                  </div>
                )}
              </Card>
            )}
          />
        )}
      </div>
    </div>
  );
};
